<template>
  <vue-auth-main>
    <template v-slot:tool>
      <div class="d-flex justify-content-end align-items-center flex-grow-1 mx-3">
        <Search class="te-search-sm" @search="search" />
      </div>
    </template>
    <template v-slot:main>
      <ListFrame>
        <template v-slot:tabs>
          <slot name="tabs" />
        </template>
        <template v-slot:toolbox>
          <ListToolBox :total-count="totalListCount" :options="listToolBoxOptions" @add="add" @reload="reload" />
        </template>
        <template v-slot:content>
          <ListSelectedBar class="d-none" :selected="selected" :options="listSelectedBarOptions" @cancel="selected = []" @batch-delete="batchDelete" />
          <ListTable
            v-model="selected"
            :listdata="listdata"
            :fields="fields"
            :custom-cell-fields="customCellFields"
            :custom-head-fields="customHeadFields"
            :get-item-index="getItemIndex"
            :get-item-key="getItemKey"
            @data-load="dataLoad"
          >
            <template v-slot:toolbar-content="{ item }">
              <ListModuleTableToolBar
                :options="getTableToolBarOptions(item)"
                @preview="preview(item)"
                @edit="edit(item)"
                @enable="enable(item)"
                @disable="disable(item)"
                @delete="del(item)"
              />
            </template>
            <template v-slot:user="{ row }">
              <div class="d-flex align-items-center">
                <div class="image-panel image-panel-1 rounded-pill mr-2">
                  <template>
                    <img v-if="row.item.userAvatar" :src="row.item.userAvatar" @error="onAvatarError">
                    <img v-else src="@/assets/images/default-avatar.png">
                  </template>
                </div>
                <div class="flex-grow-1">
                  <h6 class="mb-0">
                    {{ row.item.userName }}
                  </h6>
                  <p class="font-size-md text-secondary mb-0">
                    {{ row.item.email }}
                  </p>
                </div>
              </div>
            </template>
            <template v-slot:head(roles)="{ row }">
              <div class="d-flex align-items-center">
                <template v-if="!filterRole.id">
                  <span>{{ row.label }}</span>
                  <a id="popover-status" href="#" class="d-blick material-icons md-18 text-secondary text-decoration-none">filter_list</a>
                </template>
                <template v-else>
                  <span class="text-success">{{ filterRole.name }}</span>
                  <a id="popover-status" href="#" class="d-blick material-icons md-18 text-secondary text-success">filter_list</a>
                </template>
                <b-popover target="popover-status" :show.sync="rolePopoverShow" triggers="focus" placement="bottom" class="px-0">
                  <div class="font-size-md">
                    <b-list-group class="py-2 overflow-auto" style="max-height: 200px;">
                      <template v-for="role in roles">
                        <b-list-group-item :key="role.id" :class="{ 'bg-light': filterRole.id === role.id }" href="#" class="border-0 py-2 d-flex align-items-center" @click="roleFilter(role)">
                          <span>{{ role.name }}</span>
                        </b-list-group-item>
                      </template>
                    </b-list-group>
                  </div>
                </b-popover>
              </div>
            </template>
            <template v-slot:roles="{ row }">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="mb-0">
                    {{ row.item.roles.map((r) => r.name).join(', ') }}
                  </p>
                </div>
              </div>
            </template>
            <template v-slot:status="{ row }">
              <div class="d-flex align-items-center">
                <template v-if="row.item.status === 0">
                  <span class="material-icons font-size-xs text-gray500 mr-1">lens</span>
                  <span>{{ $t('auth.disabled') }}</span>
                </template>
                <template v-else-if="row.item.status === 1">
                  <span class="material-icons font-size-xs text-success mr-1">lens</span>
                  <span>{{ $t('auth.activating') }}</span>
                </template>
                <template v-else-if="row.item.status === 2">
                  <span class="material-icons font-size-xs text-success mr-1">lens</span>
                  <span>{{ $t('auth.inviting') }}</span>
                </template>
              </div>
            </template>
          </ListTable>
          <ListPagination v-model="currentPage" :pages="pages" />
        </template>
      </ListFrame>
      <UserInvite v-if="adding" @close="closeAdd" @confirm="LoadListDataAsync" />
      <UserPreview v-if="previewing" :user-id="targetUserId" @close="closePreview" />
      <UserSetting v-if="editing" :user-id="targetUserId" @close="closeEdit" @confirm="confirmEdit" />
    </template>
  </vue-auth-main>
</template>

<script>
import {
  ListModuleFrame as ListFrame,
  ListModulePagination as ListPagination,
  ListModuleSelectedBar as ListSelectedBar,
  ListModuleToolBox as ListToolBox,
  ListModuleTable as ListTable,
  ListModuleTableToolBar
} from '@telexpress/vue-list-module';
import Search from '@/components/Search.vue';
import UserInvite from '@/components/Auth/User/UserInvite.vue';
import UserPreview from '@/components/Auth/User/UserPreview.vue';
import UserSetting from '@/components/Auth/User/UserSetting.vue';
import PermissionService from '@/services/PermissionService';
import defaultAvatar from '@/assets/images/default-avatar.png';
import { VerifyAuthorizationAsync, GetPermissionsAsync } from '@/services/AuthorizationService';

export default {
  components: {
    Search,
    ListFrame,
    ListToolBox,
    ListSelectedBar,
    ListTable,
    ListPagination,
    ListModuleTableToolBar,
    UserInvite,
    UserPreview,
    UserSetting
  },
  data() {
    const vm = this;

    return {
      // settings
      listSelectedBarOptions: {
        buttons: ['cancel'],
        tools: []
      },
      listToolBoxOptions: {
        buttons: [],
        tools: ['reload']
      },
      listTableToolBarOptions: {
        tools: ['preview']
      },
      listCount: 10, // 每頁個數

      selected: [],
      sort: {
        key: '',
        desc: false
      },
      customCellFields: [
        {
          key: 'user', label: vm.$t('auth.account'), class: 'align-middle', thClass: 'border-0'
        },
        {
          key: 'roles', label: vm.$t('auth.role'), class: 'align-middle text-nowrap', thClass: 'border-0'
        },
        {
          key: 'status', label: vm.$t('auth.status'), class: 'align-middle', thClass: 'border-0'
        }
      ],
      customHeadFields: [
        {
          key: 'roles', label: vm.$t('auth.role')
        }
      ],
      currentPage: 1,
      roles: [],
      keyword: '',
      rolePopoverShow: false,
      filterRole: {},

      listdata: [],
      totalListCount: 0,

      adding: false,

      targetUserId: '',
      previewing: false,
      editing: false
    };
  },
  computed: {
    fields() {
      const vm = this;
      const fields = [
        {
          key: 'selected', label: '', class: 'align-middle text-center', thClass: 'border-0', thAttr: { width: '10%' }
        },
        {
          key: 'user', label: vm.$t('auth.account'), class: 'align-middle', thClass: 'border-0', thAttr: { width: '50%' }
        },
        {
          key: 'roles', label: vm.$t('auth.role'), class: 'align-middle text-nowrap', thClass: 'border-0', thAttr: { width: '25%' }
        },
        {
          key: 'status', label: vm.$t('auth.status'), class: 'align-middle align-middle', thClass: 'border-0', thAttr: { width: '15%' }, sortable: true
        }
      ];

      if (!this.previewing) {
        fields.push({
          key: 'toolbar', label: '', class: 'align-middle text-nowrap td-toolbar', tdClass: 'position-relative', thClass: 'border-0', thAttr: { width: '0' }
        });
      }

      return fields;
    },
    pages() {
      const pages = Math.ceil(this.totalListCount / this.listCount);
      return pages > 0 ? pages : 1;
    }
  },
  watch: {
    currentPage() {
      this.LoadListDataAsync();
    },
    sort(val, oldVal) {
      if (val.key !== oldVal.key || val.desc !== oldVal.desc) {
        this.LoadListDataAsync();
      }
    }
  },
  created() {
    // 設置操作權限
    this.setPermissions().then(this.updatePermissionButtons);
    this.LoadListDataAsync();
    this.LoadRolesAsync();
  },
  methods: {
    // 設置操作權限
    async setPermissions() {
      const feature = 'ManageUser';
      const verified = await VerifyAuthorizationAsync(feature);
      if (verified) {
        const permissions = await GetPermissionsAsync(feature);
        this.$store.commit('auth/setPermissions', permissions);
      } else {
        this.$store.commit('auth/setPermissions', []);
      }
    },
    // 更新操作權限功能顯示
    updatePermissionButtons() {
      const permissions = this.$store.getters['auth/permissions'];

      // 編輯
      if (permissions.includes('edit')) {
        this.listToolBoxOptions.buttons.push('add');
        this.listTableToolBarOptions.tools.push('edit');
        this.listTableToolBarOptions.tools.push('enable');
        this.listTableToolBarOptions.tools.push('disable');
      }
      // 刪除
      if (permissions.includes('delete')) {
        this.listSelectedBarOptions.tools.push('delete');
        this.listTableToolBarOptions.tools.push('delete');
      }
    },
    async LoadListDataAsync() {
      await this.$loading(async () => {
        const offset = (this.currentPage - 1) * this.listCount;
        const result = await PermissionService.GetCompanyUsersAsync(this.listCount, offset, this.sort.key, this.sort.desc, { roleId: this.filterRole.id, keyword: this.keyword });
        this.listdata = result.data;
        this.totalListCount = result.totalCount;
      });
    },
    // 取得角色清單用以做權限過濾
    async LoadRolesAsync() {
      await this.$loading(async () => {
        const roles = await PermissionService.GetRolesAsync();
        this.roles = roles.data.map((role) => ({ id: role.id, name: role.name }));
      });
    },
    getItemIndex(item) {
      return this.selected.indexOf(item.userId);
    },
    getItemKey(item) {
      return item.userId;
    },
    // 權限種類篩選
    roleFilter(role) {
      if (this.filterRole === role) {
        this.filterRole = {};
      } else {
        this.filterRole = role;
      }
      this.rolePopoverShow = false;

      // 判斷是否已在第一頁
      if (this.currentPage === 1) {
        // 重新取得資料
        this.LoadListDataAsync();
      } else {
        // 設置頁數為第一頁(同時觸發 currentPage watch)
        this.currentPage = 1;
      }
    },
    add() {
      this.adding = true;
    },
    closeAdd() {
      this.adding = false;
    },
    reload() {
      this.LoadListDataAsync();
    },
    // 批量刪除
    async batchDelete() {
      const message = this.$t('auth.userSetting.confirmDelete');
      const confirmed = await this.$popup.confirmAsync(message);

      if (confirmed) {
        await PermissionService.DeleteCompanyUsersAsync(this.selected);
        this.selected = [];
        this.LoadListDataAsync();
      }
    },
    // 刪除
    async del(item) {
      const message = this.$t('auth.userSetting.confirmDelete');
      const confirmed = await this.$popup.confirmAsync(message);

      if (confirmed) {
        await PermissionService.DeleteCompanyUsersAsync([item.userId]);
        this.$notify.deleteSuccess();
        this.LoadListDataAsync();
      }
    },
    preview(item) {
      this.targetUserId = item.userId;
      this.previewing = true;
    },
    closePreview() {
      this.targetUserId = '';
      this.previewing = false;
    },
    edit(item) {
      this.targetUserId = item.userId;
      this.editing = true;
    },
    closeEdit() {
      this.targetUserId = '';
      this.editing = false;
    },
    confirmEdit() {
      this.closeEdit();
      this.LoadListDataAsync();
    },
    async disable(item) {
      await PermissionService.UpdateCompanyUserStatusAsync(item.userId, 0);
      this.$notify.saveSuccess();
      this.LoadListDataAsync();
    },
    async enable(item) {
      await PermissionService.UpdateCompanyUserStatusAsync(item.userId, 1);
      this.$notify.saveSuccess();
      this.LoadListDataAsync();
    },
    // 錯誤頭像處理
    onAvatarError(e) {
      e.target.src = defaultAvatar;
    },
    dataLoad(options) {
      this.sort = {
        key: options.sortBy || '',
        desc: options.sortDesc || false
      };
    },
    search(val) {
      this.keyword = val;
      this.LoadListDataAsync();
    },
    // 取得每列工具列設置
    getTableToolBarOptions(item) {
      if (item.status === 0) {
        return {
          tools: [...this.listTableToolBarOptions.tools.filter((button) => button !== 'disable')]
        };
      }
      return {
        tools: [...this.listTableToolBarOptions.tools.filter((button) => button !== 'enable')]
      };
    }
  }
};
</script>
